export const SMARTWORK_PROJECT_SERVICES_STORAGE_KEY = "openanalyst.smartwork.projectServices.v1"
export const SMARTWORK_PROJECT_SERVICES_CHANGED_EVENT = "openanalyst:smartwork-project-services-changed"

export type SmartworkProjectServicesMode = "local" | "enabled"

export type SmartworkProjectServicesPreference = {
  mode: SmartworkProjectServicesMode
  displayName?: string
  projectTitle?: string
  displayIdentity?: {
    title?: string
    namespace?: string
  }
  updatedAt?: string
}

function readJsonObject(key: string) {
  if (typeof localStorage === "undefined") return {}
  const raw = localStorage.getItem(key)
  if (!raw) return {}
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {}
    return parsed as Record<string, unknown>
  } catch {
    return {}
  }
}

function writeJsonObject(key: string, value: unknown) {
  if (typeof localStorage === "undefined") return
  localStorage.setItem(key, JSON.stringify(value))
}

function notifySmartworkProjectServicesChanged() {
  if (typeof window === "undefined") return
  window.dispatchEvent(new Event(SMARTWORK_PROJECT_SERVICES_CHANGED_EVENT))
}

export function readSmartworkProjectServices() {
  return Object.fromEntries(
    Object.entries(readJsonObject(SMARTWORK_PROJECT_SERVICES_STORAGE_KEY)).flatMap(([directory, value]) => {
      if (!value || typeof value !== "object" || Array.isArray(value)) return []
      const item = value as Record<string, unknown>
      const mode: SmartworkProjectServicesMode = item.mode === "enabled" ? "enabled" : "local"
      const rawDisplayIdentity = item.displayIdentity as Record<string, unknown>
      const displayIdentity =
        rawDisplayIdentity && typeof rawDisplayIdentity === "object" && !Array.isArray(rawDisplayIdentity)
          ? {
              ...(typeof rawDisplayIdentity.title === "string" && rawDisplayIdentity.title.trim()
                ? { title: rawDisplayIdentity.title.trim() }
                : {}),
              ...(typeof rawDisplayIdentity.namespace === "string" && rawDisplayIdentity.namespace.trim()
                ? { namespace: rawDisplayIdentity.namespace.trim() }
                : {}),
            }
          : null
      return [
        [
          directory,
          {
            mode,
            ...(typeof item.displayName === "string" && item.displayName.trim()
              ? { displayName: item.displayName.trim() }
              : {}),
            ...(typeof item.projectTitle === "string" && item.projectTitle.trim()
              ? { projectTitle: item.projectTitle.trim() }
              : {}),
            ...(displayIdentity ? { displayIdentity } : {}),
            ...(typeof item.updatedAt === "string" && item.updatedAt.trim() ? { updatedAt: item.updatedAt.trim() } : {}),
          } satisfies SmartworkProjectServicesPreference,
        ],
      ]
    }),
  ) as Record<string, SmartworkProjectServicesPreference>
}

export function writeSmartworkProjectServices(value: Record<string, SmartworkProjectServicesPreference>) {
  writeJsonObject(SMARTWORK_PROJECT_SERVICES_STORAGE_KEY, value)
  notifySmartworkProjectServicesChanged()
}

export function subscribeSmartworkProjectServicesChanged(listener: () => void) {
  if (typeof window === "undefined") return () => {}
  const onStorage = (event: StorageEvent) => {
    if (event.key && event.key !== SMARTWORK_PROJECT_SERVICES_STORAGE_KEY) return
    listener()
  }
  window.addEventListener(SMARTWORK_PROJECT_SERVICES_CHANGED_EVENT, listener)
  window.addEventListener("storage", onStorage)
  return () => {
    window.removeEventListener(SMARTWORK_PROJECT_SERVICES_CHANGED_EVENT, listener)
    window.removeEventListener("storage", onStorage)
  }
}

export function smartworkProjectServicesEnabledFor(
  services: Record<string, SmartworkProjectServicesPreference>,
  directory: string,
) {
  return services[directory]?.mode === "enabled"
}
